import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SeoMetadata from "@/components/common/SeoMetadata";
import { saveSeoMetadata } from "@/utils/seoUtils";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Shield, Key, Gauge, Clock, CheckCircle, FileText, GripVertical } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import ImageOverlay from "@/components/common/ImageOverlay";
import Image from "@/components/ui/image";
import { useIsMobile } from "@/hooks/use-mobile";
import AffiliateAds from "@/components/common/AffiliateAds";

const seoKeywords = [
  "moottoripyörälaina", "mp rahoitus", "moottoripyörän osamaksu",
  "prätkälaina", "mp laina", "moottoripyörän rahoitus",
  "edullinen moottoripyörälaina", "moottoripyörälainat", "uusi moottoripyörä",
  "käytetty moottoripyörä", "moottoripyörän hankinta", "moottoripyörän vakuutus",
  "halvin moottoripyörälaina", "vakuudeton moottoripyörälaina"
];

const MoottoripyoralainaPage = () => {
  const seoTitle = "Moottoripyörälaina | Kilpailuta edullinen MP-rahoitus | Lainasampo";
  const seoDescription = "Kilpailuta moottoripyörälaina helposti ja nopeasti. Vertaile eri lainatarjouksia ja löydä edullisin rahoitus moottoripyörällesi.";
  const pageUrl = "https://lainasampo.fi/lainat/moottoripyoralaina";
  const isMobile = useIsMobile();

  useEffect(() => {
    saveSeoMetadata("/lainat/moottoripyoralaina", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
      />
      
      <div className="relative h-[60vh] md:h-[70vh] min-h-[400px] md:min-h-[500px] max-h-[500px] md:max-h-[600px]">
        <Image
          src="/lovable-uploads/856c52d5-a090-47ee-a3d8-82b1ece65478.png"
          alt="BMW klassikkomoottoripyörä"
          className="absolute inset-0 w-full h-full object-cover object-[center_40%]"
          fallbackSrc="/placeholder.svg"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent md:from-black/50 md:via-black/30 flex items-center">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-2xl text-white space-y-3 md:space-y-5 pt-0 md:pt-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight drop-shadow-lg">
                Hanki <span className="text-blue-300">moottoripyörä</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl drop-shadow-md text-blue-100">
                Kilpailuta moottoripyörälaina ilmaiseksi. Vertaile tarjouksia ja löydä paras rahoitus.
              </p>
              <div className="pt-1 md:pt-3 flex flex-col sm:flex-row gap-3 md:gap-4">
                <LainaCta
                  text={isMobile ? "Hae lainaa" : "Hae moottoripyörälainaa"}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg transform hover:scale-105 transition duration-300"
                  variant="primary"
                  size={isMobile ? "md" : "lg"}
                />
                {!isMobile && (
                  <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 backdrop-blur-sm text-white font-medium transition-all duration-300">
                    Vertaile lainoja <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 -mt-10 sm:-mt-12 md:-mt-24 mb-6 md:mb-16 relative z-10">
          {[
            { text: isMobile ? "Päätös 24h" : "Lainapäätös 24h sisällä", icon: <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Korot alk. 4,9%" : "Edulliset korot alkaen 4,9%", icon: <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "5 000€ - 70 000€" : "Lainasumma 5 000€ - 70 000€", icon: <Shield className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "1-10v maksuaika" : "Maksuaika 1-10 vuotta", icon: <Gauge className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> }
          ].map((item, i) => (
            <div key={i} className="bg-white px-3 py-2 md:px-6 md:py-3 rounded-full shadow-md flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
              {item.icon}
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-16">
          <div className="lg:col-span-2 space-y-8 md:space-y-12">
            <section className="bg-blue-50/50 py-8 md:py-12 rounded-xl">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-8 relative text-center px-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-400 bg-blue-50/80 py-1 px-4 rounded-lg inline-block">
                  Miksi valita moottoripyörälaina?
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-1/2 transform -translate-x-1/2 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    icon: <Sparkles className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Nopea rahoitus",
                    description: "Hanki moottoripyörä nopeasti joustavalla rahoituksella."
                  },
                  {
                    icon: <Shield className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Vakuudeton laina",
                    description: "Ei tarvetta vakuuksille tai takaajille."
                  },
                  {
                    icon: <Key className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Joustavat ehdot",
                    description: "Valitse sopiva laina-aika ja kuukausierä."
                  }
                ].map((feature, index) => (
                  <div key={index} className="relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl transform group-hover:scale-105 transition-transform duration-300"></div>
                    <Card className="h-full bg-transparent border-0 relative z-10 bg-white/95 backdrop-blur-sm group-hover:bg-white/100 transition-all">
                      <CardContent className="pt-4 md:pt-6">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center mb-3 md:mb-4 group-hover:shadow-lg transition-all">
                          {feature.icon}
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-blue-800 mb-1 md:mb-2">{feature.title}</h3>
                        <p className="text-sm md:text-base text-gray-700">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <div className="relative rounded-xl overflow-hidden mb-4 md:mb-6 group transform hover:scale-[1.01] transition-all duration-300">
                  <Image
                    src="/lovable-uploads/2a0724b1-fc8d-4809-92d8-a9ace1c21d49.png"
                    alt="Moottoripyöräilijä nauttii ajamisesta"
                    className="w-full h-[200px] md:h-[300px] object-cover"
                    fallbackSrc="/placeholder.svg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <h3 className="text-white text-lg md:text-xl font-bold">Vapaus ja seikkailu</h3>
                  </div>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Tunne vapauden tuuli kasvoillasi</h2>
                <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                  Moottoripyöräily on enemmän kuin pelkkä harrastus; se on elämäntapa. Koe unohtumattomia hetkiä ja nauti vapaudesta.
                </p>
                <div className="space-y-2 md:space-y-3">
                  <h3 className="font-bold text-blue-700 text-sm md:text-base">Vinkkejä moottoripyöräilijöille:</h3>
                  <ul className="space-y-1 md:space-y-2">
                    {[
                      "Hanki asianmukaiset varusteet",
                      "Huolehdi moottoripyörän huollosta",
                      "Aja turvallisesti ja vastuullisesti",
                      "Tutustu uusiin reitteihin ja kohteisiin"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm md:text-base text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <div className="relative rounded-xl overflow-hidden mb-4 md:mb-6 group transform hover:scale-[1.01] transition-all duration-300">
                  <Image
                    src="/lovable-uploads/c23f64b8-92b8-48ba-be3c-30ddb1ab7651.png"
                    alt="Moottoripyöräilijä suojavarusteissa"
                    className="w-full h-[200px] md:h-[300px] object-cover"
                    fallbackSrc="/placeholder.svg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <h3 className="text-white text-lg md:text-xl font-bold">Yhteisöllisyys ja tapahtumat</h3>
                  </div>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Löydä uusia ystäviä ja jaa kokemuksia</h2>
                <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                  Moottoripyöräily yhdistää ihmisiä. Osallistu tapahtumiin, kerhoihin ja retkiin.
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 md:p-5 rounded-xl border border-blue-200">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-sm md:text-base">Vinkkejä yhteisölliseen moottoripyöräilyyn</h3>
                  <div className="space-y-2 md:space-y-3">
                    {[
                      { label: "Liity moottoripyöräkerhoon", value: "Uusia ystäviä ja tapahtumia" },
                      { label: "Osallistu ajoretkiin", value: "Tutustu uusiin paikkoihin" },
                      { label: "Jaa kokemuksia", value: "Sosiaalinen media ja foorumit" },
                      { label: "Huolehdi turvallisuudesta", value: "Ryhmässä ajaminen" }
                    ].map((item, i) => (
                      <div key={i} className="grid grid-cols-2 text-sm md:text-base border-b border-blue-200 pb-2 last:border-0 last:pb-0">
                        <span className="text-gray-700 font-medium">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-400">
                  Suosituimmat moottoripyöräilyreitit Suomessa
                </span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    url: "/lovable-uploads/87914363-869b-4eb8-8e41-c8112c159885.png",
                    title: "Ahvenanmaan saaristotie"
                  },
                  {
                    url: "/lovable-uploads/1f172f04-fcbc-43f4-a792-31a57d0ca875.png",
                    title: "Saimaan kanavan reitti"
                  },
                  {
                    url: "/lovable-uploads/f5b7b39b-8082-465e-b338-cedb98f5f086.png",
                    title: "Lapin tunturitie"
                  }
                ].map((item, i) => (
                  <div key={i} className="relative overflow-hidden rounded-xl aspect-video group">
                    <Image
                      src={item.url}
                      alt={item.title}
                      className="w-full h-[200px] md:h-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      fallbackSrc="/placeholder.svg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                      <h3 className="text-white text-lg md:text-xl font-bold">{item.title}</h3>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mt-4">
                {[
                  "/lovable-uploads/6b685b62-4834-4069-987b-e9ccd0aa634d.png",
                  "/lovable-uploads/0fdfa0c4-caba-42ec-ba84-e3b7149ba009.png", 
                  "/lovable-uploads/38e54537-8ba2-4b27-beac-75955544c55f.png",
                  "/lovable-uploads/856c52d5-a090-47ee-a3d8-82b1ece65478.png"
                ].map((url, i) => (
                  <div key={i} className="relative overflow-hidden rounded-lg aspect-square group">
                    <Image
                      src={url}
                      alt="Moottoripyörä"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      fallbackSrc="/placeholder.svg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"></div>
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#pattern)" />
                    <defs>
                      <pattern id="pattern" patternUnits="userSpaceOnUse" width="10" height="10">
                        <circle cx="5" cy="5" r="1" fill="white" />
                      </pattern>
                    </defs>
                  </svg>
                </div>
                <div className="relative p-6 text-white">
                  <h2 className="text-2xl font-bold mb-4">Kilpailuta moottoripyörälaina nyt!</h2>
                  <p className="mb-6">
                    Moottoripyörän rahoituksessa voit säästää jopa 30% kokonaiskustannuksissa kilpailuttamalla!
                  </p>
                  <LainaCta
                    variant="primary"
                    size="lg"
                    text="Hae moottoripyörälainaa"
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-md"
                  />
                </div>
              </div>

              <Card className="border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <CardTitle className="text-lg text-white">Moottoripyörälaina lyhyesti</CardTitle>
                </div>
                <CardContent className="pt-5">
                  <div className="space-y-3">
                    {[
                      { label: "Laina-aika:", value: "1-10 vuotta" },
                      { label: "Lainasumma:", value: "5 000€ - 70 000€" },
                      { label: "Korko alkaen:", value: "4,9%" },
                      { label: "Vakuus:", value: "Vakuudeton" },
                      { label: "Käsittelyaika:", value: "1-3 arkipäivää" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Säästövinkki:</span> Kilpailuttamalla voit säästää jopa 30% rahoituksen kokonaiskustannuksissa!
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="font-semibold text-lg mb-4 text-gray-800">Katso myös nämä rahoitusvaihtoehdot</h3>
                <div className="space-y-3">
                  {[
                    { name: "Autolaina", url: "/lainat/autolaina" },
                    { name: "Venelaina", url: "/lainat/venelaina" },
                    { name: "Caravanrahoitus", url: "/lainat/caravanrahoitus" },
                    { name: "Kulutusluotto", url: "/lainat/kulutusluotto" }
                  ].map((option, i) => (
                    <Link
                      key={i}
                      to={option.url}
                      className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 font-medium transition-colors group"
                    >
                      <span>{option.name}</span>
                      <ArrowRight className="ml-auto h-4 w-4 text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl md:rounded-2xl overflow-hidden mb-8 md:mb-16 relative">
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path fill="white" d="M47.1,-57.8C58.5,-45.7,63.7,-28.7,66.8,-11.1C70,6.5,71.1,24.5,63.5,38.6C55.9,52.7,39.6,62.8,22.2,67.6C4.9,72.3,-13.5,71.6,-29.6,64.7C-45.8,57.8,-59.6,44.7,-68.5,28C-77.4,11.3,-81.3,-9,-74.5,-25.3C-67.8,-41.6,-50.5,-54,-33.7,-65.1C-16.9,-76.2,-0.6,-86,14.8,-83.5C30.3,-81,35.7,-69.9,47.1,-57.8Z" />
            </svg>
          </div>
          <div className="relative p-5 md:p-8 text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 md:mb-4">Hae moottoripyörälainaa helposti ja nopeasti!</h2>
            <p className="text-sm md:text-lg mb-4 md:mb-6 max-w-2xl">
              Kilpailuta lainatarjoukset ilmaiseksi ja ilman sitoumuksia. Saat lainapäätöksen nopeasti ja voit valita
              juuri sinulle sopivan rahoitusvaihtoehdon.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <LainaCta
                text={isMobile ? "Hae rahoitusta" : "Hae moottoripyörälainaa"}
                className="bg-white text-blue-700 hover:bg-blue-50 font-bold"
                variant="primary"
                size={isMobile ? "md" : "lg"}
              />
              {!isMobile && (
                <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-blue-700 hover:bg-blue-800 border border-blue-500 text-white font-medium transition-all duration-300">
                  Vertaile lainatuotteita <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MoottoripyoralainaPage;
